import React, { useEffect, useState, useCallback } from "react";
import Modal from "./Modal";
import CobroExistenteDialog from "./CobroExistenteDialog";
import { api, formatApiError } from "../lib/api";
import { formatoDinero, previsualizarLinea, deCentavos, aCentavos } from "../lib/money";
import {
  FORMAS_PAGO, TEXTOS, anticipoPorAplicar, etiquetaAplicar, pagoInicialConAnticipo,
} from "../lib/anticipos";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Receipt, Ban, FileText, Wallet } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useEstadoCfdi, claveCfdi } from "../lib/cfdi";
import FacturarCobro from "./cfdi/FacturarCobro";

// Modal de cobro de una cita (Control financiero, FASE 2 entrega 1).
//
// 🔴 LO QUE ESTA PANTALLA NO HACE, PORQUE LO HACE EL MOTOR:
//   · no calcula el total del cobro: viene calculado del backend (la vista `cobros_estado`)
//   · no manda `total` de linea: es GENERATED en `cobro_lineas`
//   · no manda `estado`: es derivado
//   · no manda `clinic_id` ni `fecha`: salen de la sesion y de la zona de la clinica
//   La unica cuenta que hace es la PREVISUALIZACION mientras se escribe, en centavos enteros
//   (ver lib/money.js), para que el usuario vea lo que va a cobrar antes de mandarlo.
//
// 🔴 UNA LINEA GUARDADA NO SE EDITA. No hay grant de UPDATE sobre `cobro_lineas`, y eso es
//    deliberado: es lo que congela el precio. La pantalla lo dice con esas palabras -- "se
//    anula el cobro y se rehace" -- y NUNCA muestra un error de permiso.

const CAMPO = "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-electric";

function lineaVacia() {
  return { concepto: "", cantidad: "1", precio_unitario: "", descuento: "0", descuento_motivo: "", doctor_service_id: null };
}

export default function CobroModal({ open, onClose, cita, cobroId, onCambio }) {
  // `cobroId` != null -> modo LECTURA de un cobro ya existente.
  // `cita` -> modo CREACION a partir de una cita.
  const [lineas, setLineas] = useState([]);
  const [formaPago, setFormaPago] = useState("");
  const [pagarAhora, setPagarAhora] = useState(false);
  const [nota, setNota] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);           // {detail, code}
  const [existente, setExistente] = useState(null);   // 409: {detail, cobro_id}
  const [cobro, setCobro] = useState(null);           // el cobro ya guardado
  const [cargando, setCargando] = useState(false);
  const [montoPago, setMontoPago] = useState("");
  // Forma del pago que se registra DESPUÉS de crear el cobro. Obligatoria sólo en la pantalla
  // (como en el anticipo); el backend la sigue aceptando vacía para no romper otros flujos.
  const [formaPagoRegistro, setFormaPagoRegistro] = useState("");
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [anulando, setAnulando] = useState(false);
  // CL.4 A2 -- anticipo recibido sin aplicar de la cita. La opcion de aplicarlo va MARCADA
  // por defecto: lo normal es descontarlo del cobro, y olvidarlo dejaria al paciente pagando
  // dos veces. El backend lo aplica en la MISMA transaccion (`aplicar_anticipo_id`).
  const [anticipo, setAnticipo] = useState(null);
  const [aplicarAnticipo, setAplicarAnticipo] = useState(true);
  const [errorAnticipo, setErrorAnticipo] = useState(false);

  const idAConsultar = cobroId || cita?.cobro_id || null;

  const cargarCobro = useCallback(async (id) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get(`/cobros/${id}`);
      setCobro(data);
    } catch (e) {
      // Nunca un catch mudo: si no se puede leer, se dice y se puede reintentar.
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setCargando(false);
    }
  }, []);

  // Precarga al abrir. Los TRES casos estan contemplados a proposito -- ver el aviso de abajo.
  useEffect(() => {
    if (!open) return;
    setError(null); setExistente(null); setCobro(null);
    setMontoPago(""); setMotivoAnulacion(""); setNota(""); setPagarAhora(false); setFormaPago("");
    setFormaPagoRegistro("");
    setAnticipo(null); setAplicarAnticipo(true); setErrorAnticipo(false);
    if (idAConsultar) { cargarCobro(idAConsultar); return; }
    // Solo si la Agenda dice que hay un anticipo RECIBIDO: se lee el vivo para conocer su
    // monto y su fecha (la Agenda solo trae el estado). Si la lectura falla se DICE, no se
    // calla: cobrar sin saberlo dejaria el anticipo sin aplicar.
    if (cita?.anticipo_estado === "recibido") {
      api.get("/anticipos", { params: { appointment_id: cita.id } })
        .then(({ data }) => setAnticipo(anticipoPorAplicar(data)))
        .catch(() => setErrorAnticipo(true));
    }
    if (!cita) { setLineas([lineaVacia()]); return; }
    if (cita.service_price != null && cita.service_name) {
      setLineas([{
        concepto: cita.service_name,
        cantidad: "1",
        precio_unitario: cita.service_price,          // CADENA, tal cual llega. Sin parseFloat.
        descuento: "0",
        descuento_motivo: "",
        // Solo viene del catalogo vigente. Con el catalogo viejo es null y el cobro no
        // agrupara por servicio en los reportes -- es correcto que no lo haga.
        doctor_service_id: cita.doctor_service_id ?? null,
      }]);
    } else {
      // Cita sin servicio asignado: no es un error, se empieza en blanco.
      setLineas([]);
    }
  }, [open, idAConsultar, cita, cargarCobro]);

  // CL.4 C: ¿facturación a pacientes disponible para esta sesión? Sólo se pregunta con un cobro
  // ya guardado a la vista. Con la bandera apagada el backend da 404 y el modal queda como hoy
  // (con el botón «Facturar — próximamente»).
  const { activeClinic, user, puede } = useAuth() || {};
  const cfdi = useEstadoCfdi(claveCfdi(activeClinic, user, open && !!cobro));

  if (!open) return null;

  const soloCatalogoViejo = cita && cita.service_price != null && cita.doctor_service_id == null;
  const sinServicio = cita && cita.service_price == null && !idAConsultar;
  const esNoAsistio = cita?.status === "No asistió";

  // Previsualizacion en CENTAVOS ENTEROS. No se usa parseFloat en ningun punto.
  const centavosPrevistos = lineas.reduce((acc, l) => {
    const c = previsualizarLinea(l);
    return c === null || acc === null ? null : acc + c;
  }, 0);

  const cambiar = (i, campo, valor) =>
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const cuerpo = {
        appointment_id: cita?.id ?? null,
        patient_id: cita?.patient_id ?? null,
        doctor_id: cita?.doctor_id ?? null,
        forma_pago: formaPago || "",
        metodo_pago: pagarAhora ? "PUE" : "PPD",
        nota,
        ...(anticipo && aplicarAnticipo ? { aplicar_anticipo_id: anticipo.id } : {}),
        lineas: lineas.map((l) => ({
          concepto: l.concepto,
          cantidad: String(l.cantidad || "1"),
          precio_unitario: String(l.precio_unitario ?? ""),
          descuento: String(l.descuento || "0"),
          descuento_motivo: l.descuento_motivo || "",
          doctor_service_id: l.doctor_service_id ?? null,
        })),
      };
      if (pagarAhora && centavosPrevistos !== null) {
        // Con anticipo aplicado, el pago inicial es lo que RESTA: el backend aplica el
        // anticipo antes del pago inicial, y pagar el total dejaria el cobro sobrepagado.
        const montoInicial = anticipo && aplicarAnticipo
          ? pagoInicialConAnticipo(centavosPrevistos, anticipo.monto)
          : deCentavos(centavosPrevistos);
        if (montoInicial) {
          // Si se registra pago, la forma es obligatoria (misma validación que el anticipo y
          // «Registrar pago»). Sin pago (no se marcó «Se paga completo ahora») no cambia nada.
          const forma = (formaPago || "").trim();
          if (!forma) { setError({ detail: TEXTOS.error_forma, code: "VALIDACION_LOCAL" }); return; }
          cuerpo.pago_inicial = { monto: montoInicial, forma_pago: forma };
        }
      }
      const { data } = await api.post("/cobros", cuerpo);
      setCobro(data);
      toast.success("Cobro registrado");
      onCambio?.();
    } catch (e) {
      const code = e?.response?.data?.code || null;
      const detail = formatApiError(e?.response?.data?.detail);
      // 🔴 Se ramifica por CODE, nunca por el texto (DT.60).
      if (code === "APPOINTMENT_ALREADY_CHARGED") {
        setExistente({ detail, cobro_id: e?.response?.data?.cobro_id || null });
      } else {
        setError({ detail, code });
      }
    } finally {
      // 🔴 El estado de carga se limpia TAMBIEN en el camino de excepcion: si esto viviera
      //    solo en la rama feliz, un fallo dejaria el boton en "Guardando..." para siempre.
      setGuardando(false);
    }
  };

  const registrarPago = async () => {
    setError(null);
    // Misma validación que el selector del anticipo (`cuerpoRegistro`): sin forma no se manda.
    const forma = (formaPagoRegistro || "").trim();
    if (!forma) { setError({ detail: TEXTOS.error_forma, code: "VALIDACION_LOCAL" }); return; }
    setGuardando(true);
    try {
      const { data } = await api.post(`/cobros/${cobro.id}/pagos`, { monto: String(montoPago), forma_pago: forma });
      setCobro(data); setMontoPago(""); setFormaPagoRegistro("");
      toast.success("Pago registrado");
      onCambio?.();
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setGuardando(false);
    }
  };

  const anular = async () => {
    setAnulando(true); setError(null);
    try {
      const { data } = await api.post(`/cobros/${cobro.id}/anular`, { motivo: motivoAnulacion });
      setCobro(data); setMotivoAnulacion("");
      toast.success("Cobro anulado — la cita queda libre para cobrarse de nuevo");
      onCambio?.();
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setAnulando(false);
    }
  };

  const anularPago = async (pagoId, motivo) => {
    setGuardando(true); setError(null);
    try {
      const { data } = await api.post(`/cobros/${cobro.id}/pagos/${pagoId}/anular`, { motivo });
      setCobro(data);
      toast.success("Pago anulado");
      onCambio?.();
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setGuardando(false);
    }
  };

  const badgeEstado = (estado) => {
    const estilos = {
      pagado: "bg-emerald-100 text-emerald-700",
      parcial: "bg-amber-100 text-amber-700",
      pendiente: "bg-slate-100 text-slate-600",
      anulado: "bg-red-100 text-red-700",
    };
    return (
      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${estilos[estado] || estilos.pendiente}`}
            data-testid="cobro-estado">
        {estado}
      </span>
    );
  };

  const botonFacturar = (
    // "Proximamente" a secas: sin tooltip que prometa fecha, sin "muy pronto".
    <button
      type="button"
      disabled
      title="Próximamente"
      className="px-3 py-2 text-sm rounded-lg border border-slate-200 text-slate-400 cursor-not-allowed inline-flex items-center gap-1.5"
      data-testid="cobro-facturar"
    >
      <FileText className="w-4 h-4" /> Facturar — próximamente
    </button>
  );

  return (
    <>
      <Modal
        open={open && !existente}
        onClose={guardando || anulando ? undefined : onClose}
        title={cobro ? "Cobro" : "Cobrar cita"}
        size="xl"
        testid="modal-cobro"
        footer={
          cobro ? (
            <>
              {!cfdi.disponible && botonFacturar}
              <button type="button" onClick={onClose}
                      className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700"
                      data-testid="cobro-cerrar">
                Cerrar
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} disabled={guardando}
                      className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                Cancelar
              </button>
              <button type="button" onClick={guardar}
                      disabled={guardando || lineas.length === 0}
                      className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                      data-testid="cobro-guardar">
                <Receipt className="w-4 h-4" />
                {guardando ? "Guardando…" : "Registrar cobro"}
              </button>
            </>
          )
        }
      >
        {cita && (
          <div className="text-sm text-slate-600 mb-4 pb-3 border-b border-slate-200">
            <span className="font-medium text-slate-900">{cita.patient_name}</span>
            {" · "}{cita.service_name || "Sin servicio"}{" · "}{cita.date} {cita.time}
            {cita.doctor_name ? ` · ${cita.doctor_name}` : ""}
          </div>
        )}

        {/* Aviso antes de cobrar una cita a la que el paciente no asistio. Es cobrable -- hay
            clinicas que cobran la ausencia -- pero no debe pasar inadvertido. */}
        {esNoAsistio && !cobro && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
               data-testid="cobro-aviso-no-asistio">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>El paciente <strong>no asistió</strong> a esta cita. Se puede cobrar de todos modos — revísalo antes de continuar.</span>
          </div>
        )}

        {soloCatalogoViejo && !cobro && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600"
               data-testid="cobro-aviso-catalogo-viejo">
            El precio sugerido viene del catálogo antiguo de servicios. Revísalo antes de cobrar.
          </div>
        )}
        {sinServicio && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600"
               data-testid="cobro-aviso-sin-servicio">
            Esta cita no tiene un servicio asignado, así que no hay nada que precargar. Añade los
            conceptos que vas a cobrar.
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
               data-testid="cobro-error">
            {error.detail}
          </div>
        )}

        {cargando ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando cobro…</div>
        ) : cobro ? (
          /* ── COBRO YA GUARDADO ───────────────────────────────────────── */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              {badgeEstado(cobro.estado)}
              <div className="text-right">
                <div className="text-xs text-slate-500">Total</div>
                {/* El total NO se calcula aqui: es el que manda el backend. */}
                <div className="font-display text-2xl font-bold" data-testid="cobro-total">
                  {formatoDinero(cobro.total)}
                </div>
              </div>
            </div>

            <ul className="border border-slate-200 rounded-xl divide-y divide-slate-100">
              {cobro.lineas.map((l) => (
                <li key={l.id} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{l.concepto}</div>
                    <div className="text-xs text-slate-500">
                      {l.cantidad} × {formatoDinero(l.precio_unitario)}
                      {aCentavos(l.descuento) > 0 && (
                        <> · <span className="text-amber-700">−{formatoDinero(l.descuento)} ({l.descuento_motivo})</span></>
                      )}
                    </div>
                  </div>
                  <div className="font-medium shrink-0">{formatoDinero(l.total)}</div>
                </li>
              ))}
            </ul>

            {/* 🔴 Con esas palabras, nunca un error de permiso. */}
            <p className="text-xs text-slate-500" data-testid="cobro-lineas-no-editables">
              Las líneas de un cobro no se editan. Si algo está mal, se anula el cobro y se rehace
              — al anularlo, la cita queda libre para cobrarse de nuevo.
            </p>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-slate-200 p-2.5">
                <div className="text-xs text-slate-500">Cobrado</div>
                <div className="font-semibold" data-testid="cobro-cobrado">{formatoDinero(cobro.cobrado)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-2.5">
                <div className="text-xs text-slate-500">Saldo</div>
                <div className="font-semibold" data-testid="cobro-saldo">{formatoDinero(cobro.saldo)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-2.5">
                <div className="text-xs text-slate-500">Fecha</div>
                <div className="font-semibold">{cobro.fecha}</div>
              </div>
            </div>

            {cobro.pagos.length > 0 && (
              <ul className="text-sm space-y-1" data-testid="cobro-pagos">
                {cobro.pagos.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg bg-slate-50">
                    <span className={p.anulado_at ? "line-through text-slate-400" : ""}>
                      {formatoDinero(p.monto)} · {p.fecha}
                      {p.anulado_at && <span className="ml-2 text-xs text-red-600">anulado: {p.anulado_motivo}</span>}
                    </span>
                    {!p.anulado_at && cobro.estado !== "anulado" && (
                      <button type="button" disabled={guardando}
                              onClick={() => {
                                const m = window.prompt("¿Por qué se anula este pago?");
                                if (m && m.trim()) anularPago(p.id, m.trim());
                              }}
                              className="text-xs text-red-600 hover:underline disabled:opacity-60"
                              data-testid={`cobro-anular-pago-${p.id}`}>
                        Anular
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {cobro.estado !== "anulado" && aCentavos(cobro.saldo) > 0 && (
              <div className="flex items-end gap-2 border-t border-slate-200 pt-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-500">Registrar pago</label>
                  <input className={CAMPO} inputMode="decimal" placeholder="0.00"
                         value={montoPago} onChange={(e) => setMontoPago(e.target.value)}
                         data-testid="cobro-monto-pago" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500">{TEXTOS.campo_forma}</label>
                  <select className={CAMPO} value={formaPagoRegistro} onChange={(e) => setFormaPagoRegistro(e.target.value)}
                          data-testid="cobro-forma-pago-registro">
                    <option value="">{TEXTOS.campo_forma_elige}</option>
                    {FORMAS_PAGO.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
                  </select>
                </div>
                <button type="button" onClick={registrarPago}
                        disabled={guardando || !montoPago}
                        className="px-3 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60"
                        data-testid="cobro-registrar-pago">
                  {guardando ? "…" : "Registrar"}
                </button>
              </div>
            )}

            {cobro.estado !== "anulado" ? (
              <div className="flex items-end gap-2 border-t border-slate-200 pt-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-500">Anular el cobro (motivo obligatorio)</label>
                  <input className={CAMPO} placeholder="Cobro duplicado, error de captura…"
                         value={motivoAnulacion} onChange={(e) => setMotivoAnulacion(e.target.value)}
                         data-testid="cobro-motivo-anulacion" />
                </div>
                <button type="button" onClick={anular}
                        disabled={anulando || !motivoAnulacion.trim()}
                        className="px-3 py-2 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 inline-flex items-center gap-1.5"
                        data-testid="cobro-anular">
                  <Ban className="w-4 h-4" /> {anulando ? "Anulando…" : "Anular"}
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                Cobro anulado: {cobro.anulado_motivo}. La cita quedó libre para cobrarse de nuevo.
              </div>
            )}

            {cfdi.disponible && (
              <FacturarCobro cobro={cobro} puede={puede} onCambio={() => cargarCobro(cobro.id)} />
            )}
          </div>
        ) : (
          /* ── COBRO NUEVO ─────────────────────────────────────────────── */
          <div className="space-y-3">
            {lineas.map((l, i) => {
              const conDescuento = (aCentavos(l.descuento) || 0) > 0;
              const previa = previsualizarLinea(l);
              return (
                <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`cobro-linea-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <label className="text-xs text-slate-500">Concepto</label>
                      <input className={CAMPO} value={l.concepto} placeholder="Consulta, procedimiento…"
                             onChange={(e) => cambiar(i, "concepto", e.target.value)}
                             data-testid={`cobro-concepto-${i}`} />
                    </div>
                    <button type="button" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}
                            className="mt-5 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-600"
                            title="Quitar concepto" data-testid={`cobro-quitar-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-slate-500">Cantidad</label>
                      <input className={CAMPO} inputMode="decimal" value={l.cantidad}
                             onChange={(e) => cambiar(i, "cantidad", e.target.value)}
                             data-testid={`cobro-cantidad-${i}`} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Precio</label>
                      <input className={CAMPO} inputMode="decimal" value={l.precio_unitario} placeholder="0.00"
                             onChange={(e) => cambiar(i, "precio_unitario", e.target.value)}
                             data-testid={`cobro-precio-${i}`} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Descuento</label>
                      <input className={CAMPO} inputMode="decimal" value={l.descuento} placeholder="0.00"
                             onChange={(e) => cambiar(i, "descuento", e.target.value)}
                             data-testid={`cobro-descuento-${i}`} />
                    </div>
                  </div>
                  {conDescuento && (
                    <div>
                      <label className="text-xs text-slate-500">
                        Motivo del descuento <span className="text-red-500">*</span>
                      </label>
                      <input className={CAMPO} value={l.descuento_motivo}
                             placeholder="Familiar del médico, promoción…"
                             onChange={(e) => cambiar(i, "descuento_motivo", e.target.value)}
                             data-testid={`cobro-descuento-motivo-${i}`} />
                    </div>
                  )}
                  {previa !== null && (
                    <div className="text-right text-xs text-slate-500">
                      Importe de la línea: <span className="font-medium text-slate-700">{formatoDinero(deCentavos(previa))}</span>
                    </div>
                  )}
                </div>
              );
            })}

            <button type="button" onClick={() => setLineas((ls) => [...ls, lineaVacia()])}
                    className="w-full py-2 text-sm rounded-lg border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5"
                    data-testid="cobro-anadir-linea">
              <Plus className="w-4 h-4" /> Añadir concepto
            </button>

            <div className="border-t border-slate-200 pt-3 space-y-2">
              {errorAnticipo && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                     data-testid="cobro-anticipo-error">
                  {TEXTOS.aplicar_error_lectura}
                </div>
              )}
              {anticipo && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 space-y-1"
                     data-testid="cobro-anticipo">
                  <label className="text-sm text-emerald-900 inline-flex items-center gap-2">
                    <input type="checkbox" checked={aplicarAnticipo}
                           onChange={(e) => setAplicarAnticipo(e.target.checked)}
                           data-testid="cobro-aplicar-anticipo" />
                    <Wallet className="w-4 h-4" />
                    <span data-testid="cobro-aplicar-anticipo-label">{etiquetaAplicar(anticipo)}</span>
                  </label>
                  {aplicarAnticipo && centavosPrevistos !== null && (
                    <div className="text-xs text-emerald-800" data-testid="cobro-anticipo-resta">
                      {TEXTOS.aplicar_resta}:{" "}
                      <span className="font-medium">
                        {formatoDinero(pagoInicialConAnticipo(centavosPrevistos, anticipo.monto) || "0.00")}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-600 inline-flex items-center gap-2">
                  <input type="checkbox" checked={pagarAhora} onChange={(e) => setPagarAhora(e.target.checked)}
                         data-testid="cobro-pagar-ahora" />
                  Se paga completo ahora
                </label>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Total a cobrar</div>
                  <div className="font-display text-xl font-bold" data-testid="cobro-previa-total">
                    {centavosPrevistos === null ? "—" : formatoDinero(deCentavos(centavosPrevistos))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_forma}</label>
                  <select className={CAMPO} value={formaPago} onChange={(e) => setFormaPago(e.target.value)}
                          data-testid="cobro-forma-pago">
                    <option value="">{TEXTOS.campo_forma_elige}</option>
                    {FORMAS_PAGO.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Nota</label>
                  <input className={CAMPO} value={nota} onChange={(e) => setNota(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <CobroExistenteDialog
        open={!!existente}
        detail={existente?.detail}
        onCerrar={() => { setExistente(null); onClose?.(); }}
        onVerCobro={() => {
          const id = existente?.cobro_id;
          setExistente(null);
          if (id) cargarCobro(id);
          else onCambio?.();   // sin id (carrera): se recarga y la Agenda lo mostrara
        }}
      />
    </>
  );
}
